import React, { useMemo, useState } from 'react';
import { Box, Grid, Alert, CircularProgress, useTheme } from '@mui/material';
import BedtimeIcon from '@mui/icons-material/Bedtime';
import EventRepeatIcon from '@mui/icons-material/EventRepeat';
import WavesIcon from '@mui/icons-material/Waves';
import { StatCard, DonutChart } from '../../../../../shared/components/charts';
import { DashboardCard, CohortGuard, MetricBreakdownPanel } from '../../../../../shared/components';
import { useMetricsStats } from '../../../../../shared/hooks';
import { toComparisonKey, getMetricDefinition } from '../../../../../shared/utils';
import type { MetricsFilters } from '../../../../../shared/api/types';
import { useSleepStats, useSleepStageDistribution } from '../hooks';

interface SleepStatsColumnProps {
  filterParams: MetricsFilters;
  isCentralAdmin: boolean;
  isAnonymised: boolean;
  /** When true, stat cards stack vertically (comparison columns). */
  stacked?: boolean;
}

/**
 * Renders Sleep Analytics' four stat cards + the Sleep Stage Breakdown donut for
 * a single filter scope. Safe to render once per comparison group.
 */
export const SleepStatsColumn: React.FC<SleepStatsColumnProps> = ({
  filterParams,
  isCentralAdmin,
  isAnonymised,
  stacked = false,
}) => {
  const theme = useTheme();
  const statsQuery = useSleepStats(filterParams);
  const distributionQuery = useSleepStageDistribution(filterParams);
  const cohortQuery = useMetricsStats({ ...filterParams, metrics: ['active_users'] });
  const activeUserCount = cohortQuery.data?.data?.active_users?.value;

  // Only one stat card's demographic breakdown is open at a time within this column.
  const [expandedMetric, setExpandedMetric] = useState<string | null>(null);
  const toggleMetric = (metricId: string) => setExpandedMetric((prev) => (prev === metricId ? null : metricId));

  const statCards = useMemo(() => {
    if (!statsQuery.data?.data) return [];
    const stats = statsQuery.data.data;
    return [
      { metricId: 'sleep_performance', metric: stats.sleep_performance, icon: BedtimeIcon },
      { metricId: 'sleep_consistency', metric: stats.sleep_consistency, icon: EventRepeatIcon },
      { metricId: 'sleep_efficiency', metric: stats.sleep_efficiency, icon: WavesIcon },
      { metricId: 'high_sleep_stress', metric: stats.high_sleep_stress, icon: WavesIcon },
    ].filter((card) => card.metric);
  }, [statsQuery.data]);

  const distributionData = useMemo(() => {
    if (!distributionQuery.data?.data?.distribution) return [];
    return distributionQuery.data.data.distribution.map((item) => ({
      label: item.label,
      value: item.value,
      color:
        item.name === 'deep' || item.name === 'Deep'
          ? theme.palette.success.main
          : item.name === 'rem' || item.name === 'REM'
            ? theme.palette.warning.main
            : item.name === 'light' || item.name === 'Light'
              ? theme.palette.info.main
              : theme.palette.error.main,
    }));
  }, [distributionQuery.data, theme]);

  const isLoading = statsQuery.isLoading || distributionQuery.isLoading;
  const statColSize = stacked ? { xs: 12, sm: 6 } : { xs: 12, sm: 6, md: 3 };

  if (statsQuery.isError) {
    return <Alert severity='error'>Failed to load sleep data.</Alert>;
  }

  return (
    <Box>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {isLoading
          ? [1, 2, 3, 4].map((i) => (
              <Grid size={statColSize} key={i}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <CircularProgress size={24} />
                </Box>
              </Grid>
            ))
          : statCards.map((stat) => {
              // Only metrics the comparison API can break down get the toggle.
              const breakdownSupported = toComparisonKey(stat.metricId) !== null;
              const open = expandedMetric === stat.metricId;
              return (
                <Grid size={statColSize} key={stat.metricId}>
                  <StatCard
                    metric={stat.metric}
                    icon={stat.icon}
                    expandable={breakdownSupported}
                    expanded={open}
                    onToggleExpand={() => toggleMetric(stat.metricId)}
                    breakdown={
                      <MetricBreakdownPanel
                        metricId={stat.metricId}
                        metricLabel={getMetricDefinition(stat.metricId)?.plainName ?? stat.metric.label}
                        unit={stat.metric.unit ?? '%'}
                        filterParams={filterParams}
                        enabled={open}
                      />
                    }
                  />
                </Grid>
              );
            })}
      </Grid>

      <DashboardCard
        title='Sleep Stage Breakdown'
        subtitle='Distribution of time spent in Awake, Light, REM, and Deep sleep phases across the workforce.'
        metricKey='sleep_stage_deep'
        expandable={false}
      >
        <CohortGuard userCount={activeUserCount} bypass={!isAnonymised} loading={statsQuery.isLoading}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260 }}>
              <CircularProgress />
            </Box>
          ) : distributionData.length > 0 ? (
            <DonutChart data={distributionData} height={260} />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 260 }}>
              <Alert severity='info'>No distribution data available</Alert>
            </Box>
          )}
        </CohortGuard>
      </DashboardCard>
    </Box>
  );
};
